export class RuleRunner {
	constructor(rules = [], i18n = (text) => text) {
		this.rules = Array.isArray(rules) ? rules : [];
		this.__ = typeof i18n === 'function' ? i18n : (text) => text;
	}

	getFailedSelectorsByRule(page, ruleKey) {
		if (!page || typeof ruleKey !== 'string' || ruleKey.length === 0) {
			return [];
		}

		const details = page?.ruleDetails?.[ruleKey];

		if (!details || typeof details !== 'object') {
			return [];
		}

		const selectors = new Set();
		const addSelector = (value) => {
			if (typeof value === 'string' && value.length > 0) {
				selectors.add(value);
			}
		};

		if (Array.isArray(details.failedItems)) {
			for (const item of details.failedItems) {
				addSelector(item?.selector);
			}
		}

		if (Array.isArray(details.invalidImages)) {
			for (const item of details.invalidImages) {
				addSelector(item?.selector);
			}
		}

		if (Array.isArray(details.invalidLinks)) {
			for (const item of details.invalidLinks) {
				addSelector(item?.selector);
			}
		}

		if (Array.isArray(details.invalidControls)) {
			for (const item of details.invalidControls) {
				addSelector(item?.selector);
			}
		}

		if (Array.isArray(details.issues)) {
			for (const item of details.issues) {
				addSelector(item?.selector);
			}
		}

		if (Array.isArray(details.checks)) {
			for (const item of details.checks) {
				if (item?.isFail) {
					addSelector(item?.selector);
				}
			}
		}

		if (Array.isArray(details.elements)) {
			for (const item of details.elements) {
				if (item?.issue) {
					addSelector(item?.selector);
				}
			}
		}

		return Array.from(selectors);
	}

	getFailedSelectorsMap(page) {
		const selectorsByRule = {};

		for (const rule of this.rules) {
			selectorsByRule[rule.key] = this.getFailedSelectorsByRule(page, rule.key);
		}

		return selectorsByRule;
	}

	normalizePages(pages) {
		return pages.map((page) => ({
			url: page.url,
			depth: page.depth,
			postId: page.postId ?? null,
			pageType: this.normalizePageType(page.pageType),
			layoutSignals: page.layoutSignals || null,
			rules: this.buildDefaultRulesMap(page.rules),
			ruleDetails: this.buildDefaultRuleDetailsMap(page.ruleDetails),
			failedSelectorsByRule: this.buildDefaultFailedSelectorsMap(page.failedSelectorsByRule),
			rulePartsByRule: this.buildDefaultRulePartsMap(page.rulePartsByRule)
		}));
	}

	normalizePageType(value) {
		const normalized = String(value || '').toLowerCase();

		if (normalized === 'home' || normalized === 'archive' || normalized === 'singular') {
			return normalized;
		}

		return 'singular';
	}

	buildDefaultRulePartsMap(existingRuleParts = {}) {
		const partsMap = {};

		for (const rule of this.rules) {
			const existingParts = existingRuleParts?.[rule.key] || {};
			const selectorParts = {};

			if (existingParts.selectorParts && typeof existingParts.selectorParts === 'object') {
				for (const [selector, labels] of Object.entries(existingParts.selectorParts)) {
					selectorParts[selector] = Array.isArray(labels)
						? Array.from(new Set(labels.filter((value) => typeof value === 'string' && value.length > 0)))
						: [];
				}
			}

			partsMap[rule.key] = {
				header: Number.isFinite(existingParts.header) ? existingParts.header : 0,
				footer: Number.isFinite(existingParts.footer) ? existingParts.footer : 0,
				page: Number.isFinite(existingParts.page) ? existingParts.page : 0,
				labels: Array.isArray(existingParts.labels)
					? Array.from(new Set(existingParts.labels.filter((value) => typeof value === 'string' && value.length > 0)))
					: [],
				selectorParts
			};
		}

		return partsMap;
	}

	buildDefaultFailedSelectorsMap(existingFailedSelectors = {}) {
		const failedSelectorsMap = {};

		for (const rule of this.rules) {
			const existingSelectors = existingFailedSelectors?.[rule.key];
			failedSelectorsMap[rule.key] = Array.isArray(existingSelectors)
				? Array.from(new Set(existingSelectors.filter((selector) => typeof selector === 'string' && selector.length > 0)))
				: [];
		}

		return failedSelectorsMap;
	}

	buildDefaultRulesMap(existingRules = {}) {
		const rulesMap = {};

		for (const rule of this.rules) {
			rulesMap[rule.key] = existingRules[rule.key] || rule.getDefaultStatus();
		}

		return rulesMap;
	}

	buildDefaultRuleDetailsMap(existingDetails = {}) {
		const detailsMap = {};

		for (const rule of this.rules) {
			detailsMap[rule.key] = existingDetails[rule.key] || null;
		}

		return detailsMap;
	}

	createDefaultPage(page) {
		return {
			...page,
			rules: { ...this.buildDefaultRulesMap(page.rules) },
			ruleDetails: { ...this.buildDefaultRuleDetailsMap(page.ruleDetails) },
			failedSelectorsByRule: { ...this.buildDefaultFailedSelectorsMap(page.failedSelectorsByRule) },
			rulePartsByRule: { ...this.buildDefaultRulePartsMap(page.rulePartsByRule) }
		};
	}

	async runOnDocument(page, documentNode) {
		const updatedPage = this.createDefaultPage(page);

		if (!documentNode) {
			for (const rule of this.rules) {
				updatedPage.rules[rule.key] = 'fail';
				updatedPage.ruleDetails[rule.key] = {
					error: this.__( 'Document is not available' )
				};
			}

			updatedPage.failedSelectorsByRule = this.buildDefaultFailedSelectorsMap();
			updatedPage.rulePartsByRule = this.buildDefaultRulePartsMap();

			return updatedPage;
		}

		for (const rule of this.rules) {
			try {
				const result = await Promise.resolve(rule.evaluate(documentNode, page.url));
				updatedPage.rules[rule.key] = result?.status || 'fail';
				updatedPage.ruleDetails[rule.key] = result?.details || null;
			} catch (error) {
				updatedPage.rules[rule.key] = 'fail';
				updatedPage.ruleDetails[rule.key] = {
					error: error instanceof Error ? error.message : this.__( 'Rule evaluation error' )
				};
			}
		}

		updatedPage.failedSelectorsByRule = this.getFailedSelectorsMap(updatedPage);
		updatedPage.rulePartsByRule = this.buildDefaultRulePartsMap(updatedPage.rulePartsByRule);

		return updatedPage;
	}
}
